import { useState, useCallback } from "react";
import { DropResult } from "react-beautiful-dnd";
import { toast } from "@/hooks/use-toast";
import { useExerciseCollapseState } from "../../../../hooks/useExerciseCollapseState";
import { useUnifiedOptimizedReorder } from "@/hooks/useUnifiedOptimizedReorder";
import { useOptimizedPersonalPlanDetector } from "@/hooks/useOptimizedPersonalPlanDetector";

interface UseDragDropHandlerProps {
  exercises: any[];
  routineId: string;
  onExerciseChange: () => void;
  showControls: boolean;
  onLocalUpdate: (exercises: any[]) => void;
}

export const useDragDropHandler = ({
  exercises,
  routineId,
  onExerciseChange,
  showControls,
  onLocalUpdate
}: UseDragDropHandlerProps) => {
  const [isDragging, setIsDragging] = useState(false);

  // Detección optimizada del plan personal
  const { isPersonalPlan } = useOptimizedPersonalPlanDetector({ routineId });

  const {
    updateCollapsedState,
    getCollapsedState,
    preserveStatesForDrag,
    restoreStatesFromDrag,
    cleanupStaleStates
  } = useExerciseCollapseState();

  // Usar el hook unificado optimizado
  const {
    reorderExercises,
    canReorder,
    hasPendingOperations,
    pendingOperationsCount,
    waitForPendingOperations,
    cancelPendingOperations
  } = useUnifiedOptimizedReorder({
    routineId,
    exercises,
    onLocalUpdate,
    isPersonalPlan
  });

  // Cleanup optimizado y menos agresivo
  const handleExerciseListChange = useCallback(() => {
    if (exercises?.length > 0) {
      const exerciseIds = exercises.map(ex => ex.id);
      // Cleanup diferido para evitar interferencias
      setTimeout(() => {
        cleanupStaleStates(routineId, exerciseIds);
      }, 3000);
    }
  }, [exercises, routineId, cleanupStaleStates]);

  const handleDragStart = useCallback(() => {
    console.log(`🎯 Optimized drag start - Personal Plan: ${isPersonalPlan}, Routine: ${routineId}`);
    setIsDragging(true);
    
    // Preservar estados eficientemente
    const exerciseIds = exercises.map(ex => ex.id);
    preserveStatesForDrag(routineId, exerciseIds);

    // Notificación eliminada
    // toast({
    //   title: "🎯 Arrastrando ejercicio",
    //   description: "Suelta en la nueva posición para reordenar",
    //   duration: 2000
    // });
  }, [preserveStatesForDrag, routineId, exercises, isPersonalPlan]);

  const handleDragEnd = useCallback(async (result: DropResult) => {
    console.log(`🎯 Unified optimized drag end - Personal Plan: ${isPersonalPlan}:`, result);
    
    setIsDragging(false);
    restoreStatesFromDrag();
    
    if (!result.destination || result.source.index === result.destination.index) {
      console.log("🎯 No reorder needed - zero operations");
      return;
    }

    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;

    console.log(`🚀 Unified reorder: ${sourceIndex} -> ${destinationIndex} (${isPersonalPlan ? 'Personal Plan' : 'Standard'})`);
    
    try {
      // Usar el reordenamiento unificado optimizado
      await reorderExercises(sourceIndex, destinationIndex);
      
      // Notificación eliminada
      // toast({
      //   title: "✅ Ejercicio reordenado",
      //   description: "El orden se actualizó correctamente",
      //   duration: 2000
      // });
      
      // Cleanup diferido para evitar interferencias con drag
      setTimeout(() => {
        handleExerciseListChange();
      }, 5000);
      
    } catch (error) {
      console.error('❌ Error during unified optimized reorder:', error);
      
      // Notificación eliminada
      // toast({
      //   variant: "destructive",
      //   title: "❌ Error",
      //   description: "No se pudo actualizar el orden de los ejercicios"
      // });
      
      // Solo en caso de error, triggear un refetch de recuperación
      setTimeout(() => {
        onExerciseChange();
      }, 1000);
    }
  }, [restoreStatesFromDrag, reorderExercises, handleExerciseListChange, isPersonalPlan]);

  // Funciones wrapper optimizadas
  const getCollapsedStateWithRoutine = useCallback((exerciseId: string) => {
    return getCollapsedState(routineId, exerciseId);
  }, [getCollapsedState, routineId]);

  const updateCollapsedStateWithRoutine = useCallback((exerciseId: string, collapsed: boolean) => {
    updateCollapsedState(routineId, exerciseId, collapsed);
  }, [updateCollapsedState, routineId]);

  // Solo permitir reordenamiento en planes personales con múltiples ejercicios
  const effectiveCanReorder = useCallback(() => {
    return canReorder() && showControls && exercises?.length > 1;
  }, [canReorder, showControls, exercises]);

  return {
    handleDragStart,
    handleDragEnd,
    updateCollapsedState: updateCollapsedStateWithRoutine,
    getCollapsedState: getCollapsedStateWithRoutine,
    canReorder: effectiveCanReorder,
    hasPendingOperations,
    pendingOperationsCount,
    isDragging,
    handleExerciseListChange,
    isPersonalPlan,
    
    // Funciones adicionales para control avanzado
    waitForPendingOperations,
    cancelPendingOperations
  };
};
